import {
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { SearchService } from '../../../../services/search.service';
import { Entity } from 'src/app/models/mvp-query-response-dto';
import { Table, TableFilterEvent, TablePageEvent } from 'primeng/table';
import { TabId } from 'src/app/enums/tab-id.enum';
import { MenuItemsService } from 'src/app/services/menu-items.service';
import { RoutingService } from 'src/app/services/routing.service';
import {
  DEFAULT_ENTITY_INDEX,
  FILTERABLE_FIELDS,
  RESULTS_TABLE_COLUMNS,
  TAB_ID,
} from 'src/app/constants';
import { MultiLangStrDto } from 'src/app/components/module-header/header-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { GaService } from 'src/app/services/ga.service';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { AuthService } from 'src/app/services/auth.service';
import { Subscription } from 'rxjs';

interface Column {
  field: string;
  filterField?: string;
  header: MultiLangStrDto;
  styleClass?: string;
  sortable?: boolean;
  filterable?: boolean;
}

@Component({
  selector: 'app-results-body-table',
  templateUrl: './results-body-table.component.html',
  styleUrls: ['./results-body-table.component.scss'],
})
export class ResultsBodyTableComponent implements OnInit, OnDestroy {
  _entities: Entity[] = [];
  entitiesWithImages: Entity[] = [];
  @Input() isLoading: boolean = false;
  @Input() isLoadingMoreResults: boolean = false;
  @Input() isErrorTooManyRows: boolean = false;
  @Input() isErrorNoPolygons: boolean = false;
  @Input() entityIndex: number = DEFAULT_ENTITY_INDEX;
  Math: any;
  internalUser: boolean = false;
  private internalUserSubscription?: Subscription;
  @Input() set entities(value: Entity[]) {
    this._entities = value.map(entity => ({
      ...entity,
      titleText: this.extractAllText(entity.title),
      descriptionText: this.extractAllText(entity.description)
    }));
    this.entitiesWithImages = this._entities.filter((entity) => !!entity.imageUrl);
  }
  get entities(): Entity[] {
    return this._entities;
  }
  @Output() updateFilteredEntities = new EventEmitter<{
    entities: Entity[];
    skipNavigate: boolean;
  }>();
  @Output() filterFromMap = new EventEmitter<boolean>();

  cols: Column[] = RESULTS_TABLE_COLUMNS;
  selectedEntity: Entity = {} as Entity;
  clickedLinks = {};
  skipNavigate = false;
  tabIndex = TabId.DEFAULT;
  protected readonly Array = Array;
  @ViewChild('dt', { static: true }) table?: Table;
  @Input() currentLanguage!: string;

  constructor(
    public searchService: SearchService,
    private routingService: RoutingService,
    public menuItemsService: MenuItemsService,
    private translate: TranslateService,
    private gaService: GaService,
    private authService: AuthService
  ) {}

  namesForTooltip(entity: Entity, type: string): any {
    if (type === undefined || type === null || type === '') return '';
    switch (type) {
      case 'licensesString':
        return entity.licenses.map((l) => l.longName).join(', ');
      case 'thesaurusString':
        return entity.thesaurus.map((t) => t.wordName).join(', ');
      case 'description':
        return entity.description;
      case 'title':
        return entity.title;
      case 'entityType':
        return entity.entityType;
      default:
        return '';
    }
  }

  public ngOnInit(): void {
      this.internalUserSubscription = this.authService.internalUser$.subscribe((isInternalUser) => {
        this.internalUser = isInternalUser;
      });

      this.cols = RESULTS_TABLE_COLUMNS.map(col => {
        if (col.field === 'title') {
          return { ...col, filterField: 'titleText' };
        }
        if (col.field === 'description') {
          return { ...col, filterField: 'descriptionText' };
        }
        return col;
      });
    
    this.searchService.selectedShapeId$.subscribe((shapeId: number) => {
      this.selectedEntity =
        this.entities.find((e) =>
          e.shapes.find((s) => s.shapeId === shapeId)
        ) ?? this.selectedEntity;
    });
    this.searchService.isLoadingResults$.subscribe(() => {
      this.clearFilters();
    });
    this.routingService.activeTabIndex$.subscribe((tabIndex: number) => {
      this.tabIndex = tabIndex;
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    const destinationTabId = new URL(window.location.href).searchParams.get(
      TAB_ID
    );
    if (
      this.tabIndex === TabId.TABLE &&
      (Number(destinationTabId) === TabId.TABLE || destinationTabId === null)
    ) {
      this.clearFilters();
      this.skipNavigate = true;
    }
  }
  extractAllText(field: any): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    
    // If it's an object, get ALL language values
    if (typeof field === 'object') {
      return Object.values(field).filter(v => typeof v === 'string').join(' ');
    }
    return '';
  }
  getSortOrder(field: string): string {
    const sortMeta =
      this.table?.sortField === field ? this.table?.sortOrder : 0;
    return sortMeta === 1
      ? 'ascending'
      : sortMeta === -1
      ? 'descending'
      : 'none';
  }

  getSortAriaLabel(field: string): string {
    return `${this.translate.instant(
      'iaa.app.results.resultsBody.resultsBodyTable.clickToSortBy'
    )} ${field}`;
  }

  handleClickDetails(entity: Entity, element: any): void {
    if (entity.linkInfo) {
      this.menuItemsService.setLastFocusedElement(element);
      this.menuItemsService.handleClickDetails(entity);
    }
    else if (entity.downloadUrl && entity.entityType === 'decision_letters' || entity.entityType === 'dana_pdf') {
      this.gaService.trackWithGA(entity.entityType, 'entityType');
      this.menuItemsService.downloadFile(entity);
    }
  }

  goToMap(rowData: Entity) {
    this.menuItemsService.goToMap(rowData);
  }
  handleRowSelect(event: any) {
  }
  handlePageChange(event: TablePageEvent) {
    // Ignore automatic page resets during streaming
    if (this.isLoadingMoreResults) {
      return;
    }
    this.routingService.updateEntityIndex(event.first);
  }
  handleFilter(event: TableFilterEvent) {
    this.updateFilteredEntities.emit({
      entities: event.filteredValue,
      skipNavigate: this.skipNavigate,
    });
    this.skipNavigate = false;
  }
  handleColumnFilter(event: any, field: string) {
    const value = (event.target as HTMLInputElement).value;
    this.table?.filter(value, field, 'contains');
  }
  clearFilters(fieldToExtract: string = '') {
    FILTERABLE_FIELDS.forEach(
      (key) =>
        key !== fieldToExtract &&
        this.table?.filters[key] &&
        this.table.filter('', key, 'contains')
    );
  }
  triggerExternalFilter(field: string, value: string | string[]) {
    if (Array.isArray(value) && value.length > 0) {
      // For array values, use 'in' filter mode
      this.table?.filter(value, field, 'in');
    } else if (typeof value === 'string' && value !== '') {
      // For single string values, use 'contains'
      this.table?.filter(value, field, 'contains');
    } else {
      // Clear filter if empty
      this.table?.filter('', field, 'contains');
    }
  }
  triggerMapFilter(entityId: number) {
    this.filterFromMap.emit(true);
    this.table?.filter(entityId, 'entityId', 'contains');
  }

  ngOnDestroy(): void {
    this.internalUserSubscription?.unsubscribe();
  }
}
